import { createContext, useContext, useMemo, type ReactNode } from 'react'
import {
  useSequencerStore,
  selectCurrentContainer,
  selectCurrentProject,
} from '@/stores/sequencerStore'
import type { ProjectContainer, Project } from '@/stores/sequencerStore'

/**
 * Werkshop shared project context.
 *
 * Threads the currently-open DAW project through every Werkshop
 * sub-surface (Sequencer, MIDI, Tools, Store, Settings) so users
 * can switch between tabs without losing their session. This
 * provider is a read/write *view* on top of `sequencerStore` —
 * the store stays the source of truth for `currentContainerId`,
 * the provider just exposes it via React context so children
 * don't each have to import the selector.
 *
 * Note: the Sequencer already uses `currentContainerId` as the
 * open-project pointer. Introducing a separate field would fork
 * state; instead we reuse what exists. Future waves that add new
 * Werkshop surfaces (Piano Roll, Automation, Sampler tool) can
 * read from `useOpenProject()` alone.
 */

interface OpenProject {
  /** Currently-open project id, or `null` if none. */
  projectId: string | null
  /** The project container (multi-bank) — null if no project open. */
  container: ProjectContainer | undefined
  /** The active project inside the active bank. Null if no project. */
  project: Project | undefined
  /** Switch to a different project. `null` clears. */
  setProjectId: (id: string | null) => void
  /** Shortcut for `setProjectId(null)`. */
  clear: () => void
}

const WerkshopContext = createContext<OpenProject | null>(null)

export function WerkshopProvider({ children }: { children: ReactNode }) {
  const projectId = useSequencerStore(s => s.currentContainerId)
  const container = useSequencerStore(selectCurrentContainer)
  const project = useSequencerStore(selectCurrentProject)
  const selectContainer = useSequencerStore(s => s.selectContainer)

  const value = useMemo<OpenProject>(
    () => ({
      projectId: projectId || null,
      container: container ?? undefined,
      project,
      setProjectId: id => selectContainer(id ?? ''),
      clear: () => selectContainer(''),
    }),
    [projectId, container, project, selectContainer],
  )

  return (
    <WerkshopContext.Provider value={value}>
      {children}
    </WerkshopContext.Provider>
  )
}

export function useOpenProject(): OpenProject {
  const ctx = useContext(WerkshopContext)
  if (!ctx) {
    throw new Error('useOpenProject() must be called inside <WerkshopProvider>')
  }
  return ctx
}
